import PageHeader from 'components/PageHeader'
import Section from 'components/Section'
import { SECTION_IDS } from 'constants/deliveryLocationSettings'
import useFlag from 'hooks/useFlag'
import { useRbacNavDisabled } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import DeliveryDefaultLogic from 'scenes/Cms/Settings/DeliveryDefaultLogic'
import OrderingConfiguration from 'scenes/Cms/Settings/OrderingConfiguration'
import PreferencesModalSetting from 'scenes/Cms/Settings/PreferencesModalSetting'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('operations_center.delivery_location_settings')
const tSettings = buildTranslate('cms.settings')

// SECTION_IDS now lives in src/constants/deliveryLocationSettings so the
// Settings nav component can share it without a cross-scene import.

// Offset the hash-target scroll so the section heading clears the sticky
// top navbar instead of hiding behind it. Pixel value matches the
// navbar's rendered height; if the navbar grows, bump this in lockstep.
const NAVBAR_OFFSET_PX = 80
const SECTION_SCROLL_STYLE: React.CSSProperties = {
  scrollMarginTop: NAVBAR_OFFSET_PX,
}

// Only conditionally-rendered sections can be "gated off" — the
// Ordering Configuration reference is always present, so a hash that
// targets it always resolves and never reaches the warn branch.
const GATEABLE_SECTION_IDS = new Set<string>([
  SECTION_IDS.deliveryDefault,
  SECTION_IDS.locationSelection,
])

const DeliveryLocationSettings = () => {
  // Defaults are the LD-outage / pre-init fallback. Production has the
  // flag ON at 100% with no targeting rules, so a real user during an LD
  // outage in prod is in a degraded state regardless — better to hide
  // the section than render it half-initialised. Matches the safer
  // default used in `Settings/index.tsx`; the matching `true` in
  // `Cms/Settings/index.tsx` is pre-existing and worth a follow-up
  // cleanup, but new code should err on the side of off-during-outage.
  const deliveryOptimizationEnabled = useFlag('delivery-optimization-mx', false)
  const preferencesModalSettingEnabled = useFlag(
    'preferences-modal-setting-enabled',
    false
  )

  // Use the same RBAC-aware check the nav and ProtectedRoute use so
  // gate decisions stay in sync across the role-based-access-control
  // flag transition (access under RBAC, manage in legacy mode).
  const orderingDisabled = useRbacNavDisabled('OrderingManagement')
  const canEditOrdering = !orderingDisabled
  const showDeliveryDefault = deliveryOptimizationEnabled && canEditOrdering
  const showLocationSelection =
    preferencesModalSettingEnabled && canEditOrdering

  // Older react-router-dom in this repo doesn't export useLocation, so
  // read window.location.hash directly. Native browser hash-scroll
  // runs before the section mounts; trigger it manually.
  //
  // Effect re-runs when section visibility resolves so a slow LD/RBAC
  // bootstrap doesn't strand the user on the un-scrolled top of the
  // page. The hashchange listener handles in-page nav (e.g. DATA-4954's
  // Settings deep links once they land) without forcing a full reload.
  // `capturedHashRef` survives re-runs so the Sentry capture fires at
  // most once per hash — bootstrap-race transitions don't spam.
  const capturedHashRef = useRef<string | null>(null)
  useEffect(() => {
    const handleHash = () => {
      const id = window.location.hash.replace(/^#/, '')
      if (!id) return
      const target = document.getElementById(id)
      if (target) {
        // Honour prefers-reduced-motion (WCAG 2.3.3) — animated scroll
        // can trigger vestibular disorders for users who've opted out.
        const reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        })
        // Move keyboard / SR focus to the target so users tab forward
        // from the deep-linked section, not from wherever the cursor
        // landed before navigation (WCAG 2.4.3). preventScroll avoids
        // a second scroll on top of the smooth one above.
        target.focus({ preventScroll: true })
        return
      }
      // Hash points at a known section that's gated off for this
      // merchant (flag off or insufficient permission). Report via
      // Sentry so support can trace "I clicked the deep link and
      // nothing happened" — but only once per hash, otherwise flag
      // re-evaluations would spam the same alert.
      if (GATEABLE_SECTION_IDS.has(id) && capturedHashRef.current !== id) {
        capturedHashRef.current = id
        errorHandler.capture(
          `[DeliveryLocationSettings] Hash target #${id} is gated off for this merchant.`,
          { hashId: id }
        )
      }
    }

    handleHash()
    const onHashChange = () => {
      // New hash — reset the dedupe latch so a freshly-clicked gated
      // deep link can still report. The replacement hash will
      // overwrite the ref if it too can't resolve.
      capturedHashRef.current = null
      handleHash()
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [showDeliveryDefault, showLocationSelection])

  return (
    <PageHeader title={t('title')} containerClassName="d-flex flex-column">
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <div className="grey-05-bkg border-top-1 pb-xxl grey-20-border flex-grow-1">
        <Container className="py-xl">
          {showDeliveryDefault && (
            <Section
              id={SECTION_IDS.deliveryDefault}
              style={SECTION_SCROLL_STYLE}
              tabIndex={-1}
              ariaLabel={tSettings('delivery_default_logic.title')}
              className="p-l mb-l"
            >
              <DeliveryDefaultLogic />
            </Section>
          )}
          {showLocationSelection && (
            <Section
              id={SECTION_IDS.locationSelection}
              style={SECTION_SCROLL_STYLE}
              tabIndex={-1}
              ariaLabel={tSettings('preferences_modal_setting.title')}
              className="p-l mb-l"
            >
              <PreferencesModalSetting />
            </Section>
          )}
          <Section
            id={SECTION_IDS.orderingConfiguration}
            style={SECTION_SCROLL_STYLE}
            tabIndex={-1}
            ariaLabel={tSettings('ordering_configuration.title')}
            className="p-l mb-l"
          >
            <OrderingConfiguration />
          </Section>
        </Container>
      </div>
    </PageHeader>
  )
}

export default DeliveryLocationSettings
